import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import CloseButton from "components/layout/CloseButton";
import FunctionalButton from "components/layout/FunctionalButton";
import AnimatedModal from "components/layout/motion/AnimatedModal";
import {
  ETHERNET_CONNECT_GREEN_BUTTON_CLASS,
  ETHERNET_CONNECT_GREEN_TITLE_CLASS,
  NETWORK_ROW_DISCONNECT_BUTTON_CLASS,
  NETWORK_ROW_DISCONNECT_TITLE_CLASS,
} from "lib/consts/networkSettingsActionButtons";

const fieldClass =
  "min-w-0 flex-1 rounded-r5 border border-black80 bg-black70 px-3 py-2.5 text-h5 text-white placeholder:text-black60 focus:border-primary2 focus:outline-none";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ManageRecipientsDialogProps {
  visible: boolean;
  initialEmails: string[];
  saving: boolean;
  onClose: () => void;
  onSave: (emails: string[]) => void | Promise<void>;
}

const ManageRecipientsDialog: React.FC<ManageRecipientsDialogProps> = ({
  visible,
  initialEmails,
  saving,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation(["settings", "common"]);
  const [emails, setEmails] = useState<string[]>([]);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setEmails([...initialEmails]);
      setDraft("");
      setError(null);
    }
  }, [visible, initialEmails]);

  const handleAdd = useCallback(() => {
    const next = draft.trim();
    if (!next) {
      setError(t("emailNotifications.recipientEmailRequired"));
      return;
    }
    if (!EMAIL_RE.test(next)) {
      setError(t("emailNotifications.recipientEmailInvalid"));
      return;
    }
    const lower = next.toLowerCase();
    if (emails.some((e) => e.toLowerCase() === lower)) {
      setError(t("emailNotifications.recipientEmailDuplicate"));
      return;
    }
    setEmails((prev) => [...prev, next]);
    setDraft("");
    setError(null);
  }, [draft, emails, t]);

  const handleRemove = useCallback((email: string) => {
    setEmails((prev) => prev.filter((e) => e !== email));
    setError(null);
  }, []);

  const handleSave = useCallback(() => {
    if (emails.length === 0) {
      setError(t("emailNotifications.atLeastOneRecipient"));
      return;
    }
    setError(null);
    void onSave(emails);
  }, [emails, onSave, t]);

  return (
    <AnimatedModal
      visible={visible}
      onClose={onClose}
      className="max-h-[min(90vh,36rem)] w-full max-w-md overflow-y-auto rounded-r10 border border-black80 bg-black90 p-4"
      zIndexClassName="z-[10050]"
    >
      <div className="mb-4 flex items-center justify-between">
        <p className="text-h4 font-semibold text-white">
          {t("emailNotifications.manageRecipientsTitle")}
        </p>
        <CloseButton onClick={onClose} className="relative shrink-0" />
      </div>

      <p className="mb-3 text-h5 text-black40">
        {t("emailNotifications.manageRecipientsHint")}
      </p>

      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          type="email"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder={t("emailNotifications.recipientEmailPlaceholder")}
          className={fieldClass}
          disabled={saving}
        />
        <FunctionalButton
          className={ETHERNET_CONNECT_GREEN_BUTTON_CLASS}
          title={t("emailNotifications.addRecipient")}
          titleClassName={ETHERNET_CONNECT_GREEN_TITLE_CLASS}
          disabled={saving}
          onClick={handleAdd}
        />
      </div>

      {emails.length === 0 ? (
        <div className="rounded-r10 border border-dashed border-black80 bg-black100/30 px-4 py-5 text-center">
          <p className="text-h5 text-black40">{t("emailNotifications.noRecipientsInList")}</p>
        </div>
      ) : (
        <ul className="overflow-hidden rounded-r10 border border-black80">
          {emails.map((email) => (
            <li
              key={email}
              className="flex items-center justify-between gap-3 border-b border-black80 px-3 py-2.5 last:border-b-0"
            >
              <p className="min-w-0 truncate text-h5 text-white">{email}</p>
              <FunctionalButton
                className={NETWORK_ROW_DISCONNECT_BUTTON_CLASS}
                title={t("emailNotifications.removeRecipient")}
                titleClassName={NETWORK_ROW_DISCONNECT_TITLE_CLASS}
                disabled={saving}
                onClick={() => handleRemove(email)}
              />
            </li>
          ))}
        </ul>
      )}

      {error ? (
        <p className="mt-3 text-h5 text-red-400" role="alert">
          {error}
        </p>
      ) : null}

      <div className="mt-4 flex items-center justify-end gap-2">
        <FunctionalButton
          className="h-8 min-h-8 rounded-r5 border border-black80 bg-black70 px-3 hover:bg-black80 disabled:opacity-50"
          title={t("common:cancel")}
          titleClassName="!normal-case !text-h5 !leading-none !font-normal !text-white"
          onClick={onClose}
          disabled={saving}
        />
        <FunctionalButton
          className="h-8 min-h-8 rounded-r5 border border-primary2 bg-primary2 px-3 hover:bg-primary1 disabled:opacity-50"
          title={saving ? t("common:saving") : t("common:save")}
          titleClassName="!normal-case !text-h5 !leading-none !font-normal !text-white"
          onClick={handleSave}
          disabled={saving}
        />
      </div>
    </AnimatedModal>
  );
};

export default ManageRecipientsDialog;
